import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, UserPlus, ShieldCheck, Trash2, Mail, Calendar, Users, UserCheck, Upload, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AddSupervisorDialog } from '@/components/admin/AddSupervisorDialog';
import { PromoteToSupervisorDialog } from '@/components/admin/PromoteToSupervisorDialog';
import { BulkSupervisorImportDialog } from '@/components/admin/BulkSupervisorImportDialog';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface Supervisor {
  id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  created_at: string;
  role_assigned_at: string;
  assigned_patients_count: number;
  agency_id: string | null;
  agency_name: string | null;
  agency_code: string | null;
}

export default function AdminSupervisorsPage() {
  const { user } = useAdminAuth();
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddSupervisor, setShowAddSupervisor] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [removingSupervisor, setRemovingSupervisor] = useState<Supervisor | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);

  const [agenciesLoaded, setAgenciesLoaded] = useState(false);

  // Load agencies first, then supervisors
  useEffect(() => {
    loadAgencies();
  }, []);

  useEffect(() => {
    if (agenciesLoaded && user) {
      loadSupervisors();
    }
  }, [user, agenciesLoaded, agencies]);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
    } finally {
      setAgenciesLoaded(true);
    }
  };

  const loadSupervisors = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Get supervisor roles first
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role, assigned_at')
        .eq('role', 'supervisor');

      if (rolesError) throw rolesError;

      const supervisorUserIds = roles?.map(r => r.user_id) || [];
      
      if (supervisorUserIds.length === 0) {
        setSupervisors([]);
        setLoading(false);
        return;
      }

      // Get profiles for supervisors
      let profilesQuery = supabase
        .from('profiles')
        .select('id, user_id, email, is_active, created_at, agency_id')
        .in('user_id', supervisorUserIds);

      // Agency admins only see supervisors in their agency
      if (user.role === 'agency_admin' && user.agency_id) {
        profilesQuery = profilesQuery.eq('agency_id', user.agency_id);
      }

      const { data: profiles, error: profilesError } = await profilesQuery;

      if (profilesError) throw profilesError;

      // Get patient assignments count for each supervisor
      const { data: assignments, error: assignmentsError } = await supabase
        .from('patient_assignments')
        .select('staff_user_id')
        .is('revoked_at', null);

      if (assignmentsError) throw assignmentsError;

      // Count assignments per supervisor
      const assignmentCounts = (assignments || []).reduce((acc, a) => {
        acc[a.staff_user_id] = (acc[a.staff_user_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Combine data with agency info
      const supervisorData = (profiles || []).map(p => {
        const role = roles?.find(r => r.user_id === p.user_id);
        const agency = agencies.find(a => a.id === p.agency_id);
        return {
          id: p.id,
          user_id: p.user_id,
          email: p.email,
          is_active: p.is_active,
          created_at: p.created_at,
          role_assigned_at: role?.assigned_at || p.created_at,
          assigned_patients_count: assignmentCounts[p.user_id] || 0,
          agency_id: p.agency_id || null,
          agency_name: agency?.name || null,
          agency_code: agency?.code || null,
        };
      });

      setSupervisors(supervisorData);
    } catch (error) {
      console.error('Error loading supervisors:', error);
      toast.error('Failed to load supervisors');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSupervisor = async () => {
    if (!removingSupervisor) return;

    setIsRemoving(true);
    try {
      // Remove supervisor role
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', removingSupervisor.user_id)
        .eq('role', 'supervisor');

      if (roleError) throw roleError;

      // Deactivate the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_active: false })
        .eq('user_id', removingSupervisor.user_id);

      if (profileError) {
        console.error('Error deactivating profile:', profileError);
      }

      toast.success(`Supervisor ${removingSupervisor.email} has been removed`);
      setSupervisors(prev => prev.filter(s => s.id !== removingSupervisor.id));
    } catch (error) {
      console.error('Error removing supervisor:', error);
      toast.error('Failed to remove supervisor');
    } finally {
      setIsRemoving(false);
      setRemovingSupervisor(null);
    }
  };

  const toggleSupervisorStatus = async (supervisor: Supervisor) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: !supervisor.is_active })
        .eq('user_id', supervisor.user_id);

      if (error) throw error;

      setSupervisors(prev => prev.map(s => 
        s.id === supervisor.id ? { ...s, is_active: !s.is_active } : s
      ));

      toast.success(`Supervisor ${supervisor.is_active ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      console.error('Error toggling supervisor status:', error);
      toast.error('Failed to update supervisor status');
    }
  };

  const filteredSupervisors = supervisors.filter(s =>
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canManageSupervisors = user?.role === 'super_admin' || user?.role === 'agency_admin';

  if (!canManageSupervisors) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="h-8 w-8" />
              Supervisor Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Add and manage supervisors for your agency
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowBulkImport(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>
            <Button variant="outline" onClick={() => setShowPromoteDialog(true)}>
              <UserCheck className="h-4 w-4 mr-2" />
              Promote User
            </Button>
            <Button onClick={() => setShowAddSupervisor(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Supervisor
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="shadow-card">
          <CardContent className="pt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Supervisors List */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Supervisors</CardTitle>
            <CardDescription>
              {filteredSupervisors.length} {filteredSupervisors.length === 1 ? 'supervisor' : 'supervisors'} in your agency
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredSupervisors.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No supervisors found</p>
                <p className="text-sm mt-1">
                  {searchTerm ? 'Try a different search term' : 'Add your first supervisor to get started'}
                </p>
                {!searchTerm && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowAddSupervisor(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Supervisor
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supervisor</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Assigned Patients</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSupervisors.map((supervisor) => (
                    <TableRow key={supervisor.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {supervisor.email.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              {supervisor.email}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <ShieldCheck className="h-3 w-3" />
                              Supervisor
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {supervisor.agency_name ? (
                          <Badge variant="outline" className="gap-1">
                            <Building2 className="h-3 w-3" />
                            <span className="max-w-[120px] truncate" title={supervisor.agency_name}>
                              {supervisor.agency_name}
                            </span>
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/admin/patients?supervisor=${supervisor.user_id}`}
                          className="inline-flex items-center gap-2 text-primary hover:underline"
                        >
                          <Users className="h-4 w-4" />
                          <span className="font-medium">{supervisor.assigned_patients_count}</span>
                          <span className="text-muted-foreground text-sm">
                            {supervisor.assigned_patients_count === 1 ? 'patient' : 'patients'}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={supervisor.is_active ? 'default' : 'secondary'}
                          className={supervisor.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : ''
                          }
                        >
                          {supervisor.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(supervisor.role_assigned_at), 'MMM d, yyyy')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleSupervisorStatus(supervisor)}
                          >
                            {supervisor.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive border-destructive/50 hover:bg-destructive/10"
                            onClick={() => setRemovingSupervisor(supervisor)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="shadow-card bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <ShieldCheck className="h-6 w-6 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-medium text-foreground">About Supervisors</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Supervisors can create and modify patient records, view triage sessions, 
                  and manage day-to-day operations for your agency. They cannot access billing 
                  or agency settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Supervisor Dialog */}
      <AddSupervisorDialog
        open={showAddSupervisor}
        onOpenChange={setShowAddSupervisor}
        onSuccess={loadSupervisors}
      />

      {/* Promote to Supervisor Dialog */}
      <PromoteToSupervisorDialog
        open={showPromoteDialog}
        onOpenChange={setShowPromoteDialog}
        onSuccess={loadSupervisors}
      />

      {/* Bulk Import Dialog */}
      <BulkSupervisorImportDialog
        open={showBulkImport}
        onOpenChange={setShowBulkImport}
        onSuccess={loadSupervisors}
        agencies={agencies}
      />

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!removingSupervisor} onOpenChange={(open) => !open && setRemovingSupervisor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Supervisor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold text-foreground">{removingSupervisor?.email}</span> as a supervisor? 
              They will lose access to supervisor functions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveSupervisor}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? 'Removing...' : 'Remove Supervisor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
